import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import { sanitizeHtml } from '../../../shared/sanitizeHtml';
import { NewsFeedItem } from '../NewsApi';
import { formatNewsDateInReadersLanguage } from '../newsDates';
import { NewsArticleLabels } from './newsFeedLabels';
import { newsArticlePaddingXPx, newsBodyLineHeightPx, newsBodyTextSx } from './newsPaneStyle';

const collapsedBodyHeightPx = 3 * newsBodyLineHeightPx;

function openLinksInNewTab(event: React.MouseEvent<HTMLElement>) {
    const link = (event.target as HTMLElement).closest('a');
    if (link && /^https?:/i.test(link.href)) {
        event.preventDefault();
        window.open(link.href, '_blank', 'noopener,noreferrer');
    }
}

function UnreadDot({ label }: { label: string }) {
    return (
        <Box
            component="span"
            sx={{
                position: 'absolute',
                left: '7px',
                top: '20px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'primary.main'
            }}
        >
            <Box component="span" sx={visuallyHidden}>
                {label}
            </Box>
        </Box>
    );
}

type NewsFeedArticleProps = {
    item: NewsFeedItem;
    labels: NewsArticleLabels;
};

export default function NewsFeedArticle({ item, labels }: NewsFeedArticleProps) {
    const bodyRef = React.useRef<HTMLDivElement>(null);
    const [isLong, setIsLong] = React.useState(false);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const isFolded = isLong && !isExpanded;
    const foldMask = isFolded ? 'linear-gradient(to bottom, black 60%, transparent)' : 'none';

    React.useLayoutEffect(() => {
        const body = bodyRef.current;
        setIsLong(
            Boolean(body) && (body as HTMLDivElement).scrollHeight > collapsedBodyHeightPx + 1
        );
    }, [item.content]);

    return (
        <Box
            component="article"
            sx={{
                position: 'relative',
                padding: `14px ${newsArticlePaddingXPx}px`,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: item.isRead ? 'transparent' : 'rgba(25, 118, 210, 0.04)'
            }}
        >
            {item.isRead ? null : <UnreadDot label={labels.newItem} />}
            <Typography sx={{ fontSize: 12, lineHeight: '18px', color: 'rgba(97, 97, 97, 1)' }}>
                {formatNewsDateInReadersLanguage(item.date)}
            </Typography>
            <Typography
                component="h3"
                sx={{
                    fontWeight: item.isRead ? 500 : 700,
                    fontSize: 16,
                    lineHeight: '22px',
                    color: 'rgba(33, 33, 33, 1)',
                    margin: '2px 0 4px'
                }}
            >
                {item.title}
            </Typography>
            <Box
                ref={bodyRef}
                onClick={openLinksInNewTab}
                sx={{
                    ...newsBodyTextSx,
                    maxHeight: isFolded ? `${collapsedBodyHeightPx}px` : 'none',
                    overflow: 'hidden',
                    WebkitMaskImage: foldMask,
                    maskImage: foldMask
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }}
            />
            {isLong ? (
                <Button
                    size="small"
                    aria-expanded={isExpanded}
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{ padding: 0, minWidth: 0, mt: 0.5, textTransform: 'none' }}
                >
                    {isExpanded ? labels.showLess : labels.readMore}
                </Button>
            ) : null}
        </Box>
    );
}
